import React, { useState } from 'react';
import OpportunityCard from './opportunities/OpportunityCard';

import {
  ArrowRight,
  CaretDown,
  CaretUp,
  Robot,
  Sparkle,
  TrendUp,
  UsersThree,
  X
} from '@phosphor-icons/react';
const OrgChartBottomDrawer = ({ 
  roleData, 
  opportunities = [],
  linkedAgents = [],
  onClose, 
  onCreateAgent,
  onViewOpportunity
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!roleData) return null;

  const hasOpportunities = opportunities.length > 0;
  const hasAgents = linkedAgents.length > 0;
  
  // Calculate total roles covered by opportunities for this role
  const totalRolesCovered = opportunities.reduce((sum, opp) => {
    return sum + (opp.applies_to_roles_count || 1);
  }, 0);

  return (
    <div 
      className={`bg-[#1A1B1E] transition-all duration-300 ${
        isExpanded ? 'max-h-[85vh]' : 'max-h-[45vh]'
      } overflow-y-auto`}
    >
      {/* Expand/Collapse Bar */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className="sticky top-0 z-10 h-8 bg-[rgba(255,255,255,0.05)] border-b border-[rgba(255,255,255,0.1)] flex items-center justify-center cursor-pointer hover:bg-[rgba(255,255,255,0.08)] transition-colors"
      >
        {isExpanded ? (
          <CaretDown size={20} className="text-[#A0AEC0]" />
        ) : (
          <CaretUp size={20} className="text-[#A0AEC0]" />
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-white mb-2">{roleData.title}</h2>
              {roleData.name && (
                <p className="text-lg text-[#E2E8F0] mb-1">{roleData.name}</p>
              )}
              <p className="text-[#A0AEC0]">{roleData.department}</p>
              {roleData.supervisorName && (
                <p className="text-sm text-[#718096] mt-2">
                  Reports to: <span className="text-[#A0AEC0]">{roleData.supervisorName}</span>
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
            >
              <X size={24} className="text-[#A0AEC0]" />
            </button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="glass rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#A0AEC0] text-sm">AI Opportunities</span>
                <Sparkle size={18} className="text-[#F59E0B]" />
              </div>
              <p className="text-3xl font-bold text-white">{opportunities.length}</p>
            </div>
            
            <div className="glass rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#A0AEC0] text-sm">Linked Agents</span>
                <Robot size={18} className="text-[#14F195]" />
              </div>
              <p className="text-3xl font-bold text-white">{linkedAgents.length}</p>
            </div>

            <div className="glass rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#A0AEC0] text-sm">Priority Score</span>
                <TrendUp size={18} className="text-[#6366F1]" />
              </div>
              <p className="text-3xl font-bold text-white">
                {opportunities.length > 0 
                  ? Math.max(...opportunities.map(o => o.priority_score || 0))
                  : '—'}
              </p>
            </div>
          </div>

          {/* Opportunities Section */}
          {hasOpportunities && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Sparkle size={20} className="text-[#F59E0B]" />
                AI Opportunities ({opportunities.length})
              </h3>
              <div className="space-y-3">
                {opportunities.map((opp, index) => (
                  <div key={opp.id} className="glass rounded-xl p-4 hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-semibold text-white mb-1">{opp.title}</h4>
                        <p className="text-sm text-[#A0AEC0] line-clamp-2 mb-3">{opp.description}</p>
                        
                        {/* Role Coverage Badge */}
                        {opp.applies_to_roles_count && opp.applies_to_roles_count > 1 && (
                          <div className="flex items-center gap-2 mb-3">
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6] text-xs font-medium">
                              <UsersThree size={12} />
                              Used by {opp.applies_to_roles_count} roles
                            </div>
                            {opp.primary_owner_role && (
                              <span className="text-xs text-[#718096]">
                                Primary: {opp.primary_owner_role}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Stats Row */}
                        <div className="flex items-center gap-4 text-xs text-[#A0AEC0]">
                          {opp.priority_score && (
                            <span className="flex items-center gap-1">
                              <TrendUp size={12} />
                              Priority: {opp.priority_score}
                            </span>
                          )}
                          {(opp.cost_savings_monthly || opp.monthly_savings) && (
                            <span>
                              ${((opp.cost_savings_monthly || opp.monthly_savings) / 1000).toFixed(0)}k/mo savings
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* View Details Button */}
                      <button
                        onClick={() => onViewOpportunity(opp)}
                        className="flex-shrink-0 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#5558E3] text-white text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        View Details
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Linked Agents Section */}
          {hasAgents && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Robot size={20} className="text-[#14F195]" />
                Linked AI Agents ({linkedAgents.length})
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {linkedAgents.map((agent) => (
                  <div 
                    key={agent.id}
                    className="glass rounded-lg p-4"
                  >
                    <h4 className="text-lg font-semibold text-white mb-1">{agent.name}</h4>
                    <p className="text-[#A0AEC0] text-sm">{agent.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Opportunities State */}
          {!hasOpportunities && !hasAgents && (
            <div className="text-center py-12">
              <UsersThree size={48} className="mx-auto mb-4 text-[#718096]" />
              <h3 className="text-xl font-bold text-white mb-2">No AI Opportunities Yet</h3>
              <p className="text-[#A0AEC0]">
                AI opportunities will appear here once analysis is complete
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgChartBottomDrawer;
